/**
 * Payment Error Codes
 * Feature: 003-jag-vill-justera
 * Purpose: Standardized error codes for payment operations
 */

/**
 * Payment error code enum
 */
export enum PaymentErrorCode {
  // Session errors
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  SESSION_ALREADY_USED = 'SESSION_ALREADY_USED',
  SESSION_NOT_FOUND = 'SESSION_NOT_FOUND',
  SESSION_INVALID = 'SESSION_INVALID',

  // Authorization errors
  AUTHORIZATION_FAILED = 'AUTHORIZATION_FAILED',
  AUTHORIZATION_DECLINED = 'AUTHORIZATION_DECLINED',
  AUTHORIZATION_EXPIRED = 'AUTHORIZATION_EXPIRED',
  AUTHORIZATION_NOT_FOUND = 'AUTHORIZATION_NOT_FOUND',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  CARD_DECLINED = 'CARD_DECLINED',

  // Payment method errors
  PAYMENT_METHOD_INVALID = 'PAYMENT_METHOD_INVALID',
  PAYMENT_METHOD_NOT_FOUND = 'PAYMENT_METHOD_NOT_FOUND',
  CARD_EXPIRED = 'CARD_EXPIRED',
  CARD_CVC_INVALID = 'CARD_CVC_INVALID',

  // Order creation errors
  ORDER_CREATION_FAILED = 'ORDER_CREATION_FAILED',
  ORDER_ALREADY_EXISTS = 'ORDER_ALREADY_EXISTS',
  INVALID_CART_DATA = 'INVALID_CART_DATA',
  STOCK_UNAVAILABLE = 'STOCK_UNAVAILABLE',

  // Network and timeout errors
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_ERROR = 'NETWORK_ERROR',
  STRIPE_API_ERROR = 'STRIPE_API_ERROR',

  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',
  INVALID_AMOUNT = 'INVALID_AMOUNT',
  INVALID_CURRENCY = 'INVALID_CURRENCY',

  // Database errors
  DATABASE_ERROR = 'DATABASE_ERROR',
  DATABASE_TIMEOUT = 'DATABASE_TIMEOUT',
  DATABASE_CONNECTION_ERROR = 'DATABASE_CONNECTION_ERROR',

  // Generic errors
  INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

/**
 * Human-readable error messages for each code
 */
export const PaymentErrorMessages: Record<PaymentErrorCode, string> = {
  // Session errors
  [PaymentErrorCode.SESSION_EXPIRED]:
    'Your payment session has expired. Please restart the checkout process.',
  [PaymentErrorCode.SESSION_ALREADY_USED]:
    'This payment session has already been used. Please start a new checkout.',
  [PaymentErrorCode.SESSION_NOT_FOUND]:
    'Payment session not found. Please restart the checkout process.',
  [PaymentErrorCode.SESSION_INVALID]:
    'Invalid payment session. Please restart the checkout process.',

  // Authorization errors
  [PaymentErrorCode.AUTHORIZATION_FAILED]:
    'Payment authorization failed. Please try again or use a different payment method.',
  [PaymentErrorCode.AUTHORIZATION_DECLINED]:
    'Your payment was declined. Please check your card details or try a different card.',
  [PaymentErrorCode.AUTHORIZATION_EXPIRED]:
    'Payment authorization has expired. Please restart the checkout process.',
  [PaymentErrorCode.AUTHORIZATION_NOT_FOUND]:
    'Payment authorization not found. Please restart the checkout process.',
  [PaymentErrorCode.INSUFFICIENT_FUNDS]:
    'Insufficient funds on your card. Please try a different payment method.',
  [PaymentErrorCode.CARD_DECLINED]:
    'Your card was declined. Please contact your bank or try a different card.',

  // Payment method errors
  [PaymentErrorCode.PAYMENT_METHOD_INVALID]:
    'Invalid payment method. Please check your card details.',
  [PaymentErrorCode.PAYMENT_METHOD_NOT_FOUND]:
    'Payment method not found. Please provide your payment details.',
  [PaymentErrorCode.CARD_EXPIRED]:
    'Your card has expired. Please use a different card.',
  [PaymentErrorCode.CARD_CVC_INVALID]:
    'Invalid security code (CVC). Please check and try again.',

  // Order creation errors
  [PaymentErrorCode.ORDER_CREATION_FAILED]:
    'Failed to create your order. Please contact support.',
  [PaymentErrorCode.ORDER_ALREADY_EXISTS]:
    'An order already exists for this payment. Please check your order history.',
  [PaymentErrorCode.INVALID_CART_DATA]:
    'Invalid cart data. Please refresh and try again.',
  [PaymentErrorCode.STOCK_UNAVAILABLE]:
    'One or more items in your cart are no longer available.',

  // Network and timeout errors
  [PaymentErrorCode.NETWORK_TIMEOUT]:
    'Payment processing timed out. Please check your bank statement before retrying.',
  [PaymentErrorCode.NETWORK_ERROR]:
    'Network error occurred. Please check your connection and try again.',
  [PaymentErrorCode.STRIPE_API_ERROR]:
    'Payment service error. Please try again or contact support.',

  // Validation errors
  [PaymentErrorCode.VALIDATION_ERROR]:
    'Validation error. Please check your information and try again.',
  [PaymentErrorCode.MISSING_REQUIRED_FIELD]:
    'Required information is missing. Please complete all fields.',
  [PaymentErrorCode.INVALID_AMOUNT]:
    'Invalid payment amount. Please contact support.',
  [PaymentErrorCode.INVALID_CURRENCY]:
    'Invalid currency. Only SEK is supported.',

  // Database errors
  [PaymentErrorCode.DATABASE_ERROR]:
    'Database error occurred. Please try again or contact support.',
  [PaymentErrorCode.DATABASE_TIMEOUT]:
    'Database timeout. Please try again.',
  [PaymentErrorCode.DATABASE_CONNECTION_ERROR]:
    'Could not connect to database. Please try again.',

  // Generic errors
  [PaymentErrorCode.INTERNAL_SERVER_ERROR]:
    'An internal error occurred. Please try again or contact support.',
  [PaymentErrorCode.UNKNOWN_ERROR]:
    'An unknown error occurred. Please try again or contact support.',
};

/**
 * Utility function to get error message for a code
 */
export function getPaymentErrorMessage(code: PaymentErrorCode): string {
  return PaymentErrorMessages[code] || PaymentErrorMessages[PaymentErrorCode.UNKNOWN_ERROR];
}

/**
 * Payment error class with structured data
 */
export class PaymentError extends Error {
  constructor(
    public code: PaymentErrorCode,
    public details?: unknown
  ) {
    super(getPaymentErrorMessage(code));
    this.name = 'PaymentError';
  }
}
